/**
 * Pure route-path translation logic. Deliberately free of Angular imports so it can be
 * exercised directly by tooling/tests without a TestBed.
 *
 * `translateText` and `LocalizeRouterService.translateRoute`, minus multi-locale URL
 * prefixing: this app runs one locale per domain (`alwaysSetPrefix: false` plus a
 * single-entry locales array), so the library's `urlPrefix` always resolved to `''` and
 * `addPrefixToUrl` only ever stripped a trailing slash.
 */

/** Prefix under which route translations live, e.g. `ROUTES.Misc_tutor_search`. */
export const ROUTE_TRANSLATION_PREFIX = 'ROUTES.';

/** A segment starting with this character is emitted verbatim, minus the marker. */
export const ROUTE_ESCAPE_PREFIX = '!';

export class RouteTranslator {
  constructor(private readonly translations: Record<string, any> | null = null) {}

  get isReady(): boolean {
    return this.translations !== null;
  }

  /**
   * Translate a single path segment, falling back to the untranslated key when there is
   * no `ROUTES.<key>` entry — the same fallback the library applied.
   */
  translateText(key: string): string {
    if (key.startsWith(ROUTE_ESCAPE_PREFIX)) {
      return key.replace(ROUTE_ESCAPE_PREFIX, '');
    }

    if (!this.translations) {
      return key;
    }

    // Fall back to the raw key only when the entry is absent. A present-but-empty value
    // is returned as-is: the translation loader strips a leading '/', so a backend link
    // of "/" legitimately becomes "". The old library behaved the same way.
    const value = lookup(this.translations, ROUTE_TRANSLATION_PREFIX + key);
    return value === undefined ? key : (value as string);
  }

  /** Translate every non-empty segment of a path, preserving any query string. */
  translatePath(path: string): string {
    const queryParts = path.split('?');
    if (queryParts.length > 2) {
      throw new Error('There should be only one query parameter block in the URL');
    }

    const translated = queryParts[0]
      .split('/')
      .map(segment => (segment.length ? this.translateText(segment) : segment))
      .join('/');

    return translated + (queryParts.length > 1 ? `?${queryParts[1]}` : '');
  }

  /**
   * Translate a routerLink-style value: either a string (`[href]`) or a segment array.
   * Non-string segments (ids, numbers) pass through untouched.
   */
  translateRoute(path: string | unknown[]): string | unknown[] {
    if (typeof path === 'string') {
      const url = this.translatePath(path);
      return path.startsWith('/') ? this.stripTrailingSlash(url) : url;
    }

    return path.map((segment, index) => {
      if (typeof segment !== 'string') {
        return segment;
      }
      const translated = this.translatePath(segment);
      return index === 0 && segment.startsWith('/')
        ? this.stripTrailingSlash(translated)
        : translated;
    });
  }

  /** Mirrors the library's `addPrefixToUrl` for the empty-prefix (single-locale) case. */
  stripTrailingSlash(url: string): string {
    const parts = url.split('?');
    parts[0] = parts[0].replace(/\/$/, '');
    return parts.join('?');
  }
}

/** Dotted-path lookup, equivalent to ngx-translate's key resolution for plain strings. */
function lookup(translations: Record<string, any>, key: string): unknown {
  let current: any = translations;
  for (const part of key.split('.')) {
    if (current === null || typeof current !== 'object' || !(part in current)) {
      return undefined;
    }
    current = current[part];
  }
  return current;
}
